import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { RealtimeChannel } from "@supabase/supabase-js";
import { safeGetItem } from "@mediatechgroup/app-version";
import { selectUser, selectIsAdmin, selectIsStaff } from "../store/slices/authSlice";
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead, deleteNotification as deleteNotificationAPI } from "../api/staffNotificationsAPI";

interface Notification {
  id: string;
  notification_type: string;
  title: string;
  message: string;
  link: string;
  related_entity_type: string;
  related_entity_id: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  browserNotificationsEnabled: boolean;
  /** `force` skips the automatic-refresh floor; pass it for user actions. */
  refreshNotifications: (force?: boolean) => Promise<void>;
  markNotificationAsRead: (id: string) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
  removeNotification: (id: string) => Promise<void>;
  requestBrowserNotificationPermission: () => Promise<boolean>;
}

const defaultContext: NotificationContextType = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  browserNotificationsEnabled: false,
  refreshNotifications: async () => {},
  markNotificationAsRead: async () => {},
  markAllNotificationsAsRead: async () => {},
  removeNotification: async () => {},
  requestBrowserNotificationPermission: async () => false,
};

export const NotificationContext = createContext<NotificationContextType>(defaultContext);

interface NotificationProviderProps {
  children: ReactNode;
}

/**
 * Floor between AUTOMATIC notification refreshes. See refreshNotifications —
 * a session called it ~3.1 times a second on 2026-09-14. User actions bypass
 * this; nothing automatic can beat it.
 */
const MIN_AUTO_REFRESH_MS = 3000;

function getBrowserNotificationPermission(): boolean {
  if (typeof window === "undefined" || !("Notification" in window)) return false;
  return Notification.permission === "granted";
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [browserNotificationsEnabled, setBrowserNotificationsEnabled] = useState(getBrowserNotificationPermission);
  // Survives re-renders of this provider, which sits above the routes and is
  // not remounted by navigation — so the floor holds across a route change.
  const lastAutoRefreshRef = useRef(0);

  useSelector(selectUser);
  const isAdmin = useSelector(selectIsAdmin);
  const isStaff = useSelector(selectIsStaff);
  // safeGetItem: this runs in the render body of a provider that wraps every
  // route, so a raw read throws during render on the public /embed/* routes
  // when the browser blocks third-party storage
  // (CLUB-CENTRAL-TENANT-4P / -4M). No token simply means unauthenticated,
  // which is already the expected state for an embed.
  const token = safeGetItem("authToken");
  useRef<RealtimeChannel | null>(null);

  const isStaffOrAdmin = isAdmin || isStaff;

  const requestBrowserNotificationPermission = useCallback(async (): Promise<boolean> => {
    if (typeof window === "undefined" || !("Notification" in window)) return false;

    if (Notification.permission === "granted") {
      setBrowserNotificationsEnabled(true);
      return true;
    }

    if (Notification.permission === "denied") {
      return false;
    }

    const result = await Notification.requestPermission();
    const granted = result === "granted";
    setBrowserNotificationsEnabled(granted);
    return granted;
  }, []);

  const refreshNotifications = useCallback(async (force: boolean = false) => {
    if (!token || !isStaffOrAdmin) return;

    // Floor between automatic refreshes.
    //
    // 2026-09-14, Gymnastics of York: a Tenant Owner's session called this
    // ~3.1 times a SECOND for over an hour — 7,551 requests in 20 minutes,
    // two per call (list + unread count) — and the club could not use the app.
    // Whatever re-ran the effect is a separate bug, still unknown: the effect
    // depends on [token, isStaffOrAdmin, refreshNotifications], and none of
    // those should change at that rate.
    //
    // This is the backstop. A user action (opening the bell) passes force and
    // is never delayed; the automatic paths cannot exceed this rate no matter
    // how often something re-runs them.
    const now = Date.now();
    if (!force && now - lastAutoRefreshRef.current < MIN_AUTO_REFRESH_MS) return;
    lastAutoRefreshRef.current = now;

    setLoading(true);
    try {
      const [notificationsRes, countRes] = await Promise.all([
        getNotifications(token, { limit: 20, unreadOnly: false, offset: 0 }),
        getUnreadCount(token),
      ]);

      if (notificationsRes?.success) {
        setNotifications(notificationsRes.data || []);
      }
      if (countRes?.success) {
        setUnreadCount(countRes.data?.count || 0);
      }
    } catch (error) {
      // Errors captured by axios interceptor
    } finally {
      setLoading(false);
    }
  }, [token, isStaffOrAdmin]);

  const markNotificationAsRead = useCallback(async (id: string) => {
    if (!token) return;

    try {
      const res = await markAsRead(token, id);
      if (res?.success) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      // Errors captured by axios interceptor
    }
  }, [token]);

  const markAllNotificationsAsRead = useCallback(async () => {
    if (!token) return;

    try {
      const res = await markAllAsRead(token);
      if (res?.success) {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      // Errors captured by axios interceptor
    }
  }, [token]);

  const removeNotification = useCallback(async (id: string) => {
    if (!token) return;

    try {
      const wasUnread = notifications.find((n) => n.id === id && !n.is_read);
      const res = await deleteNotificationAPI(token, id);
      if (res?.success) {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        if (wasUnread) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      // Errors captured by axios interceptor
    }
  }, [token, notifications]);

  // Fetch notifications on mount and when user/token changes
  useEffect(() => {
    if (token && isStaffOrAdmin) {
      refreshNotifications();
    }
  }, [token, isStaffOrAdmin, refreshNotifications]);

  // DISABLED: Realtime subscriptions on the tenant DB were exhausting the
  // PostgreSQL connection pool, causing the tenant database to become
  // unresponsive. Falling back to polling only until Realtime is stabilized.
  // useEffect(() => { ... Realtime subscription ... }, [token, isStaffOrAdmin, user?.id]);

  // Fallback polling every 5 minutes (safety net for Realtime disconnects)
  useEffect(() => {
    if (!token || !isStaffOrAdmin) return;

    const FALLBACK_INTERVAL = 300000; // 5 minutes

    const timerId = setInterval(() => {
      getUnreadCount(token)
        .then((res) => {
          if (res?.success) {
            setUnreadCount(res.data?.count || 0);
          }
        })
        .catch(() => {
          // Silent failure — don't crash the app
        });
    }, FALLBACK_INTERVAL);

    return () => clearInterval(timerId);
  }, [token, isStaffOrAdmin]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        browserNotificationsEnabled,
        refreshNotifications,
        markNotificationAsRead,
        markAllNotificationsAsRead,
        removeNotification,
        requestBrowserNotificationPermission,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};
